import { supabase } from '@/integrations/supabase/client';

/** Legacy hook used by DigitalCloset, StyleMagazine, ShoppingRecommendations */
export const useGetDashboardPath = () => '/dashboard';

/** Single source of truth for stage → dashboard routing */
export const stageDashboardMap: Record<string, string> = {
  signed_up: '/onboarding',
  onboarding_quiz_complete: '/dashboard1',
  call_completed: '/dashboard1',
  closet_ready: '/dashboard1',
  first_style_drop_ready: '/dashboard2',
  second_style_drop_ready: '/dashboard3',
  third_style_drop_ready: '/dashboard3',
  fourth_style_drop_ready: '/dashboard3',
  steady_state: '/dashboard4',
};

/**
 * Where a customer's lookbook lives: the Lookbook tab of their stage's dashboard. /style-magazine
 * used to be a standalone page for this; the outfits are on the dashboards and nowhere else now.
 * Stages before the first drop have no lookbook, so they get their dashboard as it is.
 */
export const lookbookPathForStage = (stage: string | null | undefined): string => {
  const base = (stage && stageDashboardMap[stage]) || '/dashboard2';
  return /^\/dashboard[2-5]$/.test(base) ? `${base}?tab=lookbook` : base;
};

/** Look up a stage in the map, default to /onboarding */
export const getStageRedirectPath = (stage: string | null): string => {
  if (stage && stageDashboardMap[stage]) {
    return stageDashboardMap[stage];
  }
  return '/onboarding';
};

/**
 * Fetch the correct dashboard path for a user.
 * Checks role (admin/stylist) first, then falls back to stage-based routing.
 */
export const fetchUserDashboardPath = async (userId: string): Promise<string> => {
  // Check if admin or stylist
  const { data: roles } = await supabase
    .from('user_roles')
    .select('role')
    .eq('user_id', userId)
    .in('role', ['admin', 'stylist']);

  if (roles && roles.length > 0) {
    return roles[0].role === 'admin' ? '/admin' : '/stylist-dashboard';
  }

  // Fetch customer stage
  const { data: stageData } = await supabase
    .from('customer_stages')
    .select('current_stage')
    .eq('user_id', userId)
    .single();

  return getStageRedirectPath(stageData?.current_stage ?? null);
};
